/**
 * The gallery portal: its destinations, its rail, and the words on the
 * inventory screen.
 *
 * A gallery member comes here to see what other people have consigned to the
 * firm and to sell it. This file is the portal spec the shell mounts and the
 * copy the inventory draws from; how a row is labelled and its code coloured
 * is `gallery.model.ts`, and the reads and the answer/sell state are
 * `useInventoryController.ts`. The other screens on the rail — sold works,
 * settlements, agents, contacts, transactions, the Sell wizard — each own a
 * constants file of their own beside their model.
 *
 * The vocabulary is INVENTORY and never COLLECTION, because a consignment is
 * a holding somebody else owns, and `GALLERY_MEMBERSHIP_LOST` is shared by
 * every id-less route here because a 403 on one means the membership behind
 * the workspace URL has gone, not that an id was mistyped.
 */

import type { CodeState } from "@ondo/shared";
import { definePortal } from "../shell/portal.ts";

/**
 * Where inside the gallery portal the reader is.
 *
 * The third portal, and the first one whose front page is a list of works the
 * workspace does not own. A collector's collection is theirs and an agent's
 * mandates reach a work and stop; a gallery's inventory is somebody else's
 * property, held to sell, under terms both sides agreed to. That relation is
 * `ondo.consignments`, and it is what every screen on this rail is a view of.
 */
export type GalleryDestination =
  | "inventory"
  | "sold"
  | "agents"
  | "settlements"
  | "borrowed"
  | "verifications"
  | "transactions"
  | "reporting"
  | "contacts"
  | "notifications"
  | "notification-preferences"
  | "about"
  | "activity"
  | "profile"
  | "work";

/**
 * A 403 on a route that takes no id is not a mistyped id. It means the caller
 * holds no gallery membership at all, which happens when a workspace URL
 * outlived the membership behind it. Inventory, sold works and settlements all
 * say it in the same words.
 */
export const GALLERY_MEMBERSHIP_LOST = "This account is no longer a member of this gallery.";

/**
 * The gallery workspace's own copy.
 *
 * Every heading here says INVENTORY rather than COLLECTION, and the difference
 * is load-bearing rather than editorial. A collection is a holding; an inventory
 * is a holding somebody else owns. Getting that word wrong on the front page
 * would be the screen making a claim about title that `ondo.consignments`
 * deliberately does not make.
 */
export const GALLERY_COPY = {
  eyebrow: "Gallery Portal",
  navLabel: "Gallery workspace",

  loading: "Loading inventory…",
  errorTitle: "Can’t load your inventory",

  /**
   * Says how a work arrives, because a gallery cannot put one there itself.
   *
   * `ondo.offer_consignment()` is the OWNER's act — only the artist or estate
   * who holds the work may consign it — so an empty screen with no explanation
   * would read as a broken workspace to the one party with no control to press.
   * The collector's empty collection and the agent's empty mandate list both
   * make this same argument in the same place.
   */
  empty: "No work is consigned to you yet.",
  emptyDetail:
    "An artist or estate consigns a work from its own record. When one does, it appears here with its terms, and you can open its full record or sell it.",

  /** Singular and plural, for the count beside the heading. */
  count: (n: number) => `${n} ${n === 1 ? "work" : "works"}`,

  /**
   * The header line the design writes as "5 green-coded works".
   *
   * GREEN-CODED, not "for sale", and the two are no longer interchangeable.
   * Green is the ladder's green — `codeStateOf` returning `never-sold`, which needs
   * no standing transfer AND a signature from an appointed authority — so this
   * is a fact about the record rather than about anybody's intention to sell.
   *
   * OND-99 NARROWED IT, AND THE NARROWING IS THE TICKET. It counted
   * `codeStateOf`, which answered green on "has not sold" alone, so the figure
   * included works no authority had signed — rows whose own mark beside them was
   * amber. The count is smaller again now, and for the second time correctly:
   * a resale is live inventory and red, and a work awaiting a determination is
   * live inventory and amber. It counts what the words say.
   */
  greenCoded: (n: number) => `live inventory · ${n} green-coded ${n === 1 ? "work" : "works"}`,

  /**
   * The badge beside a row's title — THE SAME STATEMENT AS THE MARK, in words.
   *
   * KEYED BY `CodeState` FROM `@ondo/shared`, which is the whole of OND-99.
   * It was two strings, `green` and `red`, chosen by a local `codeStateOf` that
   * read `sold` and nothing else — so an unsold work nobody with authority had
   * signed showed "Green code" beside an amber mark captioned "awaiting
   * determination", and a registrar asking which one was true had no answer.
   * Both were right about different questions, which is not an answer a gallery
   * will accept on a £2.2m picture. One calculation now answers both: the model
   * passes the row's facts to `codeStateOf` once and the badge renders its verdict.
   *
   * THE COLOUR IS NAMED BECAUSE THIS SCREEN COMPUTED IT. `ConsignedWork` carries
   * both facts the ladder needs, so the row is entitled to say which colour the
   * mark came out — and must, because at `size="row"` the mark prints no word of
   * its own and this badge is the whole of layer 4. The estate's studio list
   * holds only `neverSold` and names no colour for exactly that reason.
   *
   * "Colour · State" is the house form, borrowed from `STUDIO_COPY` rather than
   * invented, and the state word is the one `STATE_WORD` prints on the mark. The
   * colour alone would fail the rule `ondoCode.ts` states twice: red and green
   * are the classic colour-blind pair and the most commercially loaded one in
   * the product, so the state is a word, always.
   */
  codeBadge: {
    "never-sold": "Green · Never sold",
    active: "Red · Active",
    awaiting: "Amber · Awaiting determination",
    museum: "Blue · Museum",
    historic: "Black · Historic",
    undisclosed: "Not disclosed",
  } as const satisfies Record<CodeState, string>,

  locked: "Locked",
  lockedDetail: "Cannot be transferred on until the covenant term expires, or the gallery unlocks it.",

  /**
   * The lead paragraph over the list.
   *
   * The design's version says green codes arrive "with provenance and
   * authenticity already established". That claim is not this registry's to
   * make: nothing is approved and everything is attested — a work exists the
   * moment somebody registers it, and what decides whether its record is worth
   * believing is the attestations on its chain of title, which the registry
   * counts and concludes nothing about. So the sentence says where the code came
   * from, which is true, and stops.
   */
  lead: "Green codes arrive from the artist or estate, who consigned the work to you.",
  leadAction: "Open any work for its full record. The primary act on each is Sell.",

  /** Terms, as the row prints them. Matched to `ondo.consignment_basis`. */
  basis: {
    net_of_sale: "net-of-sale",
    min_price_agreed: "min. price agreed",
    unspecified: "",
  },
  primaryRepresentation: "Primary — represented artist",
  consignedOn: "Consigned",
  issuedBy: "Issuing estate",
  sell: "Sell",
  open: "View",

  /**
   * Why the Sell button is disabled, on the button itself.
   *
   * THE REASON HAS CHANGED TWICE, and the copy has changed with it both times
   * rather than being left to go quietly wrong. It first named a missing table;
   * `ondo.transfers` landed. It then named a missing form; this is that form.
   * What remains is the only honest reason a control can be dead: the SERVER
   * says no. `capabilities.transferWork` is `exists (select 1 from
   * ondo.sellable_work_ids())` — a live accepted consignment, held at `editor`
   * or above — so a member below that rung, or one whose consignments have all
   * ended, sees this sentence rather than a button that fails when pressed.
   *
   * Names the refusal rather than saying "coming soon", which is the whole of
   * the `notYet` pattern applied to one control.
   */
  sellNotYet:
    "This account cannot record a sale for this gallery. Selling needs a live consignment the gallery has accepted, and membership at director level or above.",

  /**
   * Shown in place of Accept and Decline, to a seat below editor.
   *
   * The OFFER still renders — knowing what has been offered is the least a
   * cataloguer needs, and hiding it would make an absence look like a policy
   * the consignor had chosen. What is withheld is the answer, because
   * `ondo.answer_consignment()` requires editor: accepting binds the gallery
   * to terms somebody else wrote.
   */
  answerNeedsEditor:
    "Accepting an offer commits the gallery to its terms, so it needs a registrar or a director. The offer and its terms are shown here for everyone.",

  /**
   * The offers a gallery has not answered, above the works it holds.
   *
   * A separate section rather than a badge on a row: deciding what to take and
   * selling what you have are two different jobs, and interleaving them makes
   * the first invisible among the second.
   */
  offersTitle: "Offered to you",
  offersDetail:
    "An artist or estate has offered you these works. Accepting opens the full record and puts the work in your inventory; declining ends the offer and tells them.",
  accept: "Accept",
  decline: "Decline",

  /**
   * The filter over the list, and it is by ARTIST.
   *
   * The design's chip rows are STATUS and CODE COLOUR, and neither is built here.
   * STATUS still cannot be: every row is a live consignment by construction,
   * because `ondo.gallery_inventory()` returns open ones only, so the chip row
   * would be one live chip and two dead ones.
   *
   * CODE COLOUR could now be — a resale is red inventory — and is still not,
   * for a different reason: it would be a filter that matches nothing on almost
   * every gallery's screen, which is the same "chrome pretending to be a
   * control" that `CollectionToolbar` refuses. It becomes a real control the
   * day a firm holds enough resales to want it, and the honest place to add it
   * is then.
   */
  artistFilter: "Artist",
  allArtists: "All",
  noMatches: "No work by that artist is consigned to you.",
} as const;

/**
 * The gallery portal.
 *
 * FIVE of its rail's items are `planned`, which is more than any portal has
 * carried, and each names a table that does not exist rather than a screen
 * nobody has written. That is the honest-placeholder pattern the agent portal
 * shipped — "a greyed-out nav item tells the reader nothing while a page naming
 * what is missing tells them something" — and the blocks below are deleted, not
 * amended, as each stage lands.
 *
 * Every subtitle names the workspace's subject, as the agent's do and the
 * collector's do not. A gallery workspace is always an organisation's: what is
 * consigned belongs to the FIRM, and a director reading "your inventory" would
 * be reading a sentence about the wrong party.
 */
export const GALLERY_PORTAL = definePortal<GalleryDestination>({
  eyebrow: GALLERY_COPY.eyebrow,
  navLabel: GALLERY_COPY.navLabel,

  destinations: [
    { id: "inventory", label: "Inventory / Sell a Work", glyph: "▤", state: "ready", group: "main" },
    { id: "sold", label: "Sold Works", glyph: "→", state: "ready", group: "main" },
    { id: "agents", label: "Agents", glyph: "◉", state: "ready", group: "main" },
    { id: "settlements", label: "Resale settlements", glyph: "⇄", state: "ready", group: "main" },
    // THE BORROWER'S HALF OF A LOAN, which had no screen anywhere until OND-9.
    // Named "Loans to you" rather than "Borrowed", so it reads as a thing that
    // has arrived rather than as a category of possession.
    { id: "borrowed", label: "Loans to you", glyph: "⇤", state: "ready", group: "main" },
    { id: "verifications", label: "Verifications", glyph: "✓", state: "ready", group: "main" },
    { id: "transactions", label: "Transaction Centre", glyph: "£", state: "ready", group: "main" },
    // OND-97. BESIDE the Transaction Centre rather than inside it: that screen
    // is the fee LEDGER, where a director acts on one row, and this one is the
    // firm's position over a period, where nothing is acted on at all. Andrew's
    // decision of 28 September asked for "a coherent reporting experience, not
    // just extra numbers added to the current tiles", and four period-filtered
    // totals bolted above a list of individual fees would have been exactly
    // that — two screens' jobs fighting over one set of filters.
    { id: "reporting", label: "Costs & Income", glyph: "◳", state: "ready", group: "main" },
    // OND-61. The collector list is behind the middle rung and behind a
    // bundle: a Scholar, a Specialist and a records seat with no named role do
    // not see this item, and the screen refuses them if they reach it by URL.
    {
      id: "contacts",
      label: "Contacts & CRM",
      glyph: "◫",
      state: "ready",
      group: "main",
      requires: "collector_list_read",
    },
    { id: "notifications", label: "Notifications", glyph: "⊘", state: "ready", group: "foot" },
    { id: "notification-preferences", label: "Notification Settings", glyph: "⚙", state: "ready", group: "foot" },
    { id: "about", label: "About ONDO", glyph: "i", state: "ready", group: "foot" },
    { id: "activity", label: "Activity & access log", glyph: "≡", state: "ready", group: "foot" },
    // Listed as well as reachable from the identity card, for the reason both
    // other rails give: a control whose only affordance is that it happens to be
    // clickable is one most people never press.
    { id: "profile", label: "My Profile", glyph: "◐", state: "ready", group: "foot" },
    { id: "work", label: "Artwork record", glyph: "◈", state: "unlisted", group: "main" },
  ],

  headers: {
    inventory: {
      title: "Inventory / Sell a Work",
      // The count belongs to the screen, which is the only thing that has
      // counted the rows. A subtitle asserting "5 works" from up here would be
      // the header answering a question it never asked the server.
      subtitle: (subject) => `${subject.name} — works consigned to you, live for sale`,
    },
    sold: {
      title: "Sold Works",
      subtitle: (subject) => `Works ${subject.name} has sold, and what each sale carries`,
    },
    agents: {
      title: "Agents",
      // Both halves now, so the subtitle stops naming one. It used to read
      // "Works assigned to X to represent", which was the INBOUND direction and
      // the only one that existed — see the comment in GalleryWorkspace for what
      // changed.
      subtitle: (subject) => `${subject.name} — buyer and seller agents`,
    },
    settlements: {
      title: "Resale settlements",
      subtitle: "What a post-sale covenant owes when a work sells again inside its term",
    },
    borrowed: {
      title: "Loans to you",
      // Says what the screen is FOR in the second clause, the way `offers`
      // does: somebody who has been offered no loan needs to know this is
      // where one would arrive, not that something is missing. And it names
      // the distinction the whole feature rests on — possession moves, title
      // does not.
      subtitle:
        "Works somebody has offered to lend you. Accepting moves where a work is and nothing about who owns it",
    },
    verifications: {
      title: "Verifications",
      subtitle: (subject) =>
        `${subject.name} — confirm or dispute ONDO information others have asked you to verify`,
    },
    transactions: {
      title: "Transaction Centre",
      subtitle: (subject) =>
        `${subject.name} — sales awaiting a buyer, fees due, and settlements outstanding`,
    },
    reporting: {
      title: "Costs & Income",
      // Names both ledgers and the thing the screen will not do. "Recorded"
      // does the work: ONDO observes no payment, so a subtitle promising what
      // has been paid and received would be the header making the one claim
      // every sentence inside the screen is written to avoid.
      subtitle: (subject) =>
        `${subject.name} — ONDO fees against resale-agreement income, by period and currency`,
    },
    contacts: {
      title: "Contacts & CRM",
      subtitle: "The people you have transacted with or added, and those who have viewed your rooms",
    },
    // Addressed to the GALLERY, which is the whole reason this screen exists
    // here — a settlement falling due is told to the firm, and no director's
    // own feed carries it.
    notifications: {
      title: "Notifications",
      subtitle: (subject) => `${subject.name} — what has been sent here, and what needs an answer`,
    },
    "notification-preferences": {
      title: "Notification Settings",
      subtitle:
        "How each kind of notification reaches you — immediately, in the daily digest, or not at all",
    },
    about: { title: "About ONDO", subtitle: "" },
    activity: {
      title: "Activity & access log",
      // The FEED half is still the caller's own trail, and that limit is
      // unchanged: `GET /v1/activity` takes a collection, a work or `scope=me`
      // and refuses to resolve a supplied id, so there is no org-scoped feed to
      // read. OND-68 did not fix that and did not need to.
      //
      // WHAT CHANGED IS THE SECOND HALF OF THE SENTENCE. The access trail IS
      // the firm's, read from `ondo.access_events` rather than from a feed —
      // append-only, undeletable by anybody including the service role, and
      // written inside the request that serves the read. The clause is hedged
      // because `access_events_read` gives an ordinary seat only their OWN
      // rows: promising every colleague's reads to a viewer would be the same
      // over-claim this subtitle has always refused, one rung down.
      subtitle:
        "Every act you have recorded, and who has opened a record — the firm's whole trail if you administer it, your own if not",
    },
    profile: {
      title: "My Profile",
      // Replaced at render on the firm's workspace, where the subject is the
      // organisation rather than the reader — see PROFILE_HEADERS and `profileAt`.
      subtitle: "Account, identity verification and membership",
    },
    work: {
      title: "Artwork record",
      subtitle: "What the registry holds for this work, and its chain of title",
    },
  },

  /**
   * What each planned destination waits on, named as a relation rather than a
   * date.
   *
   * EMPTY. Three entries have stood here and each left when its table landed:
   * `offers` with `ondo.transfers`, `settlements` with `ondo.resale_settlements`,
   * and last `transactions` with `ondo.registration_fees` (20260910110000) —
   * the one every migration since the transfer had deferred by name. DELETED
   * rather than amended, as each stage lands; a page naming what is missing is
   * only honest while something is. The map stays because the type is total
   * over `planned` destinations and the next one will need it.
   */
  notYet: {},

  defaultDestination: "inventory",

  records: {
    inventory: "none",
    sold: "none",
    agents: "none",
    settlements: "none",
    borrowed: "none",
    verifications: "none",
    transactions: "none",
    reporting: "none",
    contacts: "none",
    notifications: "none",
    "notification-preferences": "none",
    about: "none",
    activity: "none",
    profile: "none",
    work: "required",
  },

  // Where a work record with no work goes. A gallery holds no collection of its
  // own — a consignment reaches the work and stops, exactly as a mandate does —
  // so the inventory is the only place a record can honestly be chosen from.
  recordFallback: "inventory",

  // THE INBOX IS THE FIRM'S, and that is why it took until now to land.
  //
  // This rail carried no notifications item and said so in a comment, on the
  // grounds that the design has none. The design is not the reason it was
  // missing: a gallery is told things at the ORGANISATION's ONDO id —
  // `settlement.due` reaches the covenant holder, `consignment.offered` the
  // gallery it was offered to — and until `GET /v1/notifications` learned
  // `?principal=`, no person signing in could open that feed at all. Adding the
  // item alone would have shown every director an empty inbox.
  //
  // What made a gallery notice a settlement before this was opening Resale
  // settlements and looking, which is exactly the "screen that reads a feed to
  // answer what is TRUE" that CLAUDE.md warns about, arrived at from the other
  // side: the fact was in Postgres and nothing carried it to anybody.
  badges: {
    verifications: "verifications",
    notifications: "notifications",
    // The badge gallery-resale-settlements.html asked for and target-settlements-
    // due.png draws. Counts the `due` group of the screen it sits beside.
    settlements: "settlements",
  },

  profileAt: "profile",

  // No greeting. This portal's front page is a list of other people's works,
  // and "Welcome back" over somebody else's property is the wrong register —
  // the agent portal declines a greeting for the same reason.
  greetsAt: null,

  // No header action. The one act this portal exists for is Sell, and it belongs
  // to a ROW rather than to the header: a portal-level "Sell" button would have
  // no work to sell. `capabilities.transferWork` now answers true for a director
  // with stock — it stopped being a literal false when `ondo.transfers` landed —
  // and what the control still waits on is the form, not the record. See
  // `sellNotYet`.
  primaryAction: () => null,
  secondaryAction: () => null,
});
